// libs
import { skipToken } from '@apollo/client/react'
import React, { useCallback, useRef } from 'react'

// api
import {
  ProjectMembershipSortingKeys,
  ProjectType,
  SortDirection,
  useProjectMemberships
} from '@lopo-tech/lopo-api'

// components
import ListWrapper from 'src/containers/smart_components/list_wrapper/list_wrapper'

// constants
import { FEED_DEFAULT_PER_PAGE } from 'src/constants/feed'

// state
import { useSelectCurrentProfile } from 'src/reducers/user/user_hooks'

// local
import { MyPublicParticipationsIcon } from 'src/components/icons/projects/public-participation/my-public-participations'
import { textResources } from 'src/lang/de'
import { COLOR_TYPE_PRIMARY, COLOR_TYPE_PRIMARY_2 } from 'src/utils/color'
import { ProjectListSection } from '../project_list_section/project_list_section'
import { ProjectListWrapper } from '../project_list_wrapper/project_list_wrapper'

const t = textResources.projects

type Props = {
  projectType: ProjectType;
}

const MyProjectList: React.FC<Props> = ({ projectType }) => {
  const currentProfile = useSelectCurrentProfile()

  const { data, loading, error, refetch, fetchMore } = useProjectMemberships(
    currentProfile?.id
      ? {
        variables: {
          memberId: currentProfile.id,
          page: 1,
          perPage: FEED_DEFAULT_PER_PAGE,
          sorting: {
            direction: SortDirection.Desc,
            fieldName: ProjectMembershipSortingKeys.ProjectLastActivityAt
          }
        }
      }
      : skipToken
  )

  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const getNextPage = useCallback(() => {
    const nextPage = data?.projectMemberships?.pageInfo?.nextPage
    if (nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: nextPage } })
    }
  }, [data?.projectMemberships?.pageInfo?.nextPage, fetchMore])

  const projects =
    data?.projectMemberships?.items?.map((membership) => membership.project) ??
    []

  const totalCount = data?.projectMemberships?.pageInfo?.totalCount ?? 0

  const showErrorState = !loading && Boolean(error)
  const showEmptyState = !loading && !error && totalCount === 0

  return (
    <ListWrapper getNextPage={getNextPage}>
      <ProjectListWrapper
        refetch={refetch}
        showEmptyState={showEmptyState}
        showErrorState={showErrorState}
        projectType={projectType}
      >
        <ProjectListSection
          icon={
            <MyPublicParticipationsIcon
              size={24}
              primaryColor={COLOR_TYPE_PRIMARY}
              secondaryColor={COLOR_TYPE_PRIMARY_2}
            />
          }
          projects={projects}
          title={t.overview.sectionMyProjects}
          count={totalCount}
        />
      </ProjectListWrapper>
    </ListWrapper>
  )
}

export default MyProjectList
